import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, User, Loader2 } from "lucide-react";

const ROLE_LABELS = {
  regional_mgr: "Regional Manager",
  property_mgr: "Property Manager",
  reception: "Reception",
  finance: "Finance",
};

/**
 * Iter48 — Staff activation page.
 *
 * Opened via the WhatsApp magic link `/staff/accept/{token}`. Shows the
 * invitation details, asks for a password (min 8 chars), then activates the
 * staff account and logs them in — landing them on the Partner Portal.
 */
export default function StaffAccept() {
  const navigate = useNavigate();
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/staff/accept/${token}`)
      .then(({ data }) => setInvite(data))
      .catch((e) => setError(e?.response?.data?.detail || "Invitation not found"))
      .finally(() => setLoading(false));
  }, [token]);

  const activate = async () => {
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords don't match.");
    setSaving(true);
    try {
      const { data } = await api.post(`/staff/accept/${token}`, { password });
      if (data?.session_token) localStorage.setItem("hw_session_token", data.session_token);
      toast.success(`Welcome, ${data?.user?.name || "team"}! Logging you in…`);
      // Force a full reload so AuthProvider re-fetches with the fresh session.
      window.location.href = "/partner";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Activation failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="max-w-md mx-auto py-16 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (error) {
    return (
      <div className="max-w-md mx-auto py-16 px-4">
        <div className="hw-card p-8 text-center">
          <div className="text-red-600 font-black text-lg">Invitation unavailable</div>
          <p className="text-sm text-slate-600 mt-2">{error}</p>
          <Button onClick={() => navigate("/login")} className="mt-4 rounded-sm">Go to login</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto py-12 px-4">
      <div className="hw-card p-8">
        <div className="flex items-center gap-2 text-emerald-700">
          <ShieldCheck className="w-5 h-5" />
          <span className="hw-label">Staff Invitation</span>
        </div>
        <h1 className="hw-head text-2xl font-black text-slate-900 mt-2">Welcome, {invite.staff_name}</h1>
        <p className="text-sm text-slate-600 mt-1">
          <b>{invite.invited_by_name || "SwaanStay"}</b> invited you to join their team as
          <b className="text-emerald-700"> {ROLE_LABELS[invite.staff_role] || invite.staff_role}</b>.
        </p>
        {invite.scoped_region && (
          <div className="text-xs text-slate-500 mt-1">Scope: <b>{invite.scoped_region}</b></div>
        )}
        {invite.scoped_hotels_count > 0 && (
          <div className="text-xs text-slate-500 mt-1">Access to: <b>{invite.scoped_hotels_count}</b> propert{invite.scoped_hotels_count === 1 ? "y" : "ies"}</div>
        )}

        <div className="mt-6 space-y-3" data-testid="staff-accept-form">
          <div>
            <Label className="hw-label">Create a password (min 8 chars)</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-sm mt-1" data-testid="staff-password" />
          </div>
          <div>
            <Label className="hw-label">Confirm password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-sm mt-1" data-testid="staff-confirm-password" />
          </div>
          <Button onClick={activate} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid="staff-activate">
            {saving ? "Activating…" : "Activate my account"}
          </Button>
          <p className="text-xs text-slate-500 text-center pt-2">
            <User className="w-3 h-3 inline mr-1" />
            Sign in later at <b>swaanstay.com/login</b> with your email (<code className="bg-slate-100 px-1">{invite.staff_email || "auto-generated"}</code>) + this password.
          </p>
        </div>
      </div>
    </div>
  );
}
